import React, { useEffect, useState } from 'react';
import { liteClient as searchClient } from 'algoliasearch/lite';
import { baseConfig } from '../../../config/algolia';
import { buildSearchFilters } from '@jbhi-fi/utils/src/buildAlgoliaSearchFilters/buildAlgoliaSearchFilters';

import { Configure, InstantSearch, Index } from 'react-instantsearch';
import { CustomStats } from '../refinements/CustomStats';
import { initWishlist } from '../../../snippets/customer.wishlist';
import { HeroBannerCarousel } from '@jbhi-fi/components/src/HeroBannerCarousel/HeroBannerCarousel';
import { Wings } from '@jbhi-fi/components/src/Wings/Wings';
import { WingsTransformer } from '@jbhi-fi/components/src/Contentful/transformers/WingsTransformer';
import CustomInfiniteHits from './CustomInfiniteHits';
import { CollectionLeaderboardBanner } from '@jbhi-fi/components/src/CollectionLeaderboardBanner/CollectionLeaderboardBanner';
import { LeaderboardBannerTransformer } from '@jbhi-fi/components/src/Contentful/transformers/LeaderboardBannerTransformer';
import { RefinementContainer } from '../refinements/RefinementContainer';
import SponsoredContentContainer from '../../../components/sponsoredcontent/SponsoredContentContainer';
import { PreezieFinder } from '@jbhi-fi/components/src/PreezieFinder/PreezieFinder';
import { Disclaimer } from '@jbhi-fi/components/src/Disclaimer';
import { ContentfulMetafieldCollection } from '@jbhi-fi/apis/src/contentful/types';
import { CollectionHeaderNav } from '@jbhi-fi/components/src/CollectionHeaderNav/CollectionHeaderNav';
import {
  createRoutingURL,
  queryStateToUrl,
  urlToSearchState,
  type AlgoliaQueryState,
} from './searchStateUrlStorageHelpers';
import { CollectionHeader } from '@jbhi-fi/components/src/CollectionHeader/CollectionHeader';
import { useContentfulCollection } from '@jbhi-fi/components/src/hooks/useContentfulCollection/useContentfulCollection';
import { PreezieBannerTransformer } from '@jbhi-fi/components/src/Contentful/transformers/PreezieBannerTransformer';
import { styles } from './CollectionCuratedContainer.css';
import { useCustomerContext } from '@jbhi-fi/components/src/context/CustomerContextProvider';
import { getPerksWasCollectionRedirected, setPerksWasCollectionRedirected } from '../perksInfo/utils';
import { usePublicApiClient } from '@jbhi-fi/components/src/hooks/usePublicApiClient';
import { useContentfulAds } from '@jbhi-fi/components/src/hooks/useContentfulAds';
import { CarouselBannerTransformer } from '@jbhi-fi/components/src/Contentful/transformers/CarouselBannerTransformer';
import { useMediaQuery } from '@jbhi-fi/lanyard-ui/utils/useMediaQuery';
import { InsightsMiddleware } from '../../../utils/algolia/InsightsMiddleware';
import { history } from 'instantsearch.js/es/lib/routers';
import { ProductClient } from '@jbhi-fi/apis/src/algolia/ProductClient';
import { SearchContentContainer } from '../../../components/searchcontent/SearchContentContainer';
import { getAnalyticsTags } from '../../../utils/algolia/search-insights';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag/useFeatureFlag';

const MAX_CAROUSEL_BANNERS = 8;

const { app_id = '', product_families_api_key = '', product_families_index = '', base_facets = [] } = baseConfig;

/**
 * Initialise the algolia search client and the index
 */
const algoliaSearchClient = searchClient(app_id, product_families_api_key, ProductClient.getAlogliaCache(app_id));
const indexName = product_families_index;

export const COLLECTION_DEBOUNCE_TIME = 400;

const CollectionContainer = (collection: ContentfulMetafieldCollection) => {
  const [shouldHide, setShouldHide] = useState(true);
  const [nbSaleItems, setNbSaleItems] = useState(0);
  const [nbClearanceItems, setNbClearanceItems] = useState(0);
  const [searchState, setSearchState] = useState(urlToSearchState(window.location, collection));
  const [debouncedSetState, setDebouncedSetState] = useState<number | undefined>(undefined);

  const shouldShowNav = !!collection?.browse?.length;
  const { contentfulThemeApiClient } = usePublicApiClient();

  const facets =
    collection.facets && Array.isArray(collection.facets) && collection.facets.length
      ? [
          ...base_facets.filter(facet => facet.facet === 'isMarketplace' || facet.facet === 'Sold by'),
          ...collection.facets,
        ]
      : [
          ...base_facets,
          {
            facet: 'Category',
            type: 'refinementList',
          },
        ];

  const onSearchStateChange = (updatedSearchStates: any) => {
    clearTimeout(debouncedSetState);

    const updatedSearchState = updatedSearchStates[indexName];

    /**
     * Filter out only the necessary parameters to sync to the URL.
     */
    const newQueryState: AlgoliaQueryState = {
      refinementList: updatedSearchState.refinementList,
      range: updatedSearchState.range,
      hitsPerPage: updatedSearchState.hitsPerPage,
      sortBy: updatedSearchState.sortBy,
      toggle: updatedSearchState.toggle,
    };

    setDebouncedSetState(
      // @ts-ignore
      setTimeout(() => {
        window.history.pushState(updatedSearchState, '', queryStateToUrl(window.location, newQueryState));
      }, COLLECTION_DEBOUNCE_TIME)
    );

    setSearchState(updatedSearchState);
  };

  useEffect(() => {
    initWishlist();
  }, []);

  const { collection: contentfulCollection, isLoading: isCollectionLoading } = useContentfulCollection({
    handle: collection.handle,
  });
  const isMerchStudio = collection.template === 'merchstudio';

  const { leaderboards, banners, wings, isLoading } = useContentfulAds({
    queryOptions: { handle: collection.handle },
    contentfulThemeApiClient,
  });
  const shouldShowWings = useMediaQuery('(min-width: 1200px)');
  const preezieFinderProps = PreezieBannerTransformer.toPreezieFinderProps(contentfulCollection?.fields?.preezieBanner);
  const preezieFinder = preezieFinderProps ? <PreezieFinder {...preezieFinderProps} /> : null;
  const { isPerks: isPerksContextual } = useCustomerContext();
  const isPerks = isPerksContextual || window.Jbhifi?.customerDetails?.isPerks;
  const shouldSplitTiles = contentfulCollection?.fields?.splitTiles;
  const algoliaPersonalizationImpact = useFeatureFlag('algoliaPersonalizationImpact') ?? 0;
  const shouldUseMerchStudioCollection = useFeatureFlag('shouldUseMerchStudioCollection') && isMerchStudio;
  const [hasNoMerchStudioHits, setHasNoMerchStudioHits] = useState(false);
  const collectionHandle = collection.handle.split('_')[0];

  useEffect(() => {
    const shouldHideContent = contentfulCollection?.fields?.allowedAccess === 'Perks members' && !isPerks;
    if (contentfulCollection) {
      setShouldHide(shouldHideContent);
      const destination = window.Jbhifi?.perksCollectionRedirectUrl || '/pages/perks?redirect=true';
      if (shouldHideContent && destination) {
        if (getPerksWasCollectionRedirected()) {
          setPerksWasCollectionRedirected(false);
        } else {
          setPerksWasCollectionRedirected(true);
          location.href = destination;
        }
      } else {
        setPerksWasCollectionRedirected(false);
      }
    }
  }, [contentfulCollection, isPerks]);

  if (shouldHide || isCollectionLoading) return <></>; // the redirect above is not instant so hide the component while we wait
  return (
    <InstantSearch
      key={indexName}
      searchClient={algoliaSearchClient}
      indexName={indexName}
      initialUiState={{
        [indexName]: searchState,
      }}
      onStateChange={({ uiState, setUiState }) => {
        onSearchStateChange(uiState);
        setUiState(uiState);
      }}
      future={{
        persistHierarchicalRootCount: false,
        preserveSharedStateOnUnmount: true,
      }}
      routing={{
        router: history({
          cleanUrlOnDispose: false,
          createURL({ routeState }) {
            return createRoutingURL(routeState);
          },
        }),
      }}
    >
      <InsightsMiddleware />
      <Configure
        clickAnalytics
        filters={
          !shouldUseMerchStudioCollection || hasNoMerchStudioHits
            ? collection.searchFilters
            : buildSearchFilters(`collectionHandles:"${collectionHandle}"`)
        }
        query={collection.query}
        distinct
        analyticsTags={getAnalyticsTags('discovery')}
        {...(algoliaPersonalizationImpact > 0
          ? {
              personalizationImpact: algoliaPersonalizationImpact,
              enablePersonalization: true,
            }
          : {})}
      />

      <Index indexId="saleitems" indexName={product_families_index}>
        <Configure
          distinct
          analytics={false}
          query={collection.query}
          filters={
            !shouldUseMerchStudioCollection || hasNoMerchStudioHits
              ? `${collection.searchFilters} AND onPromotion:true`
              : buildSearchFilters(`collectionHandles:"${collectionHandle} AND onPromotion:true"`)
          }
        />
        <CustomStats setNbItems={setNbSaleItems} nbItems={nbSaleItems} />
      </Index>

      <Index indexId="clearance" indexName={product_families_index}>
        <Configure
          distinct
          analytics={false}
          query={collection.query}
          filters={
            !shouldUseMerchStudioCollection || hasNoMerchStudioHits
              ? `${collection.searchFilters} AND banner_tags.label:Clearance`
              : buildSearchFilters(`collectionHandles:"${collectionHandle} AND banner_tags.label:Clearance"`)
          }
        />
        <CustomStats setNbItems={setNbClearanceItems} nbItems={nbClearanceItems} />
      </Index>

      {isLoading || banners?.length ? (
        <HeroBannerCarousel
          {...CarouselBannerTransformer.toHeroBannerCarouselProps(banners.slice(0, MAX_CAROUSEL_BANNERS), {
            variant: 'collection',
          })}
          isLoading={isLoading}
          variant="collection"
        />
      ) : null}
      {shouldShowWings && wings ? <Wings {...WingsTransformer.toWingsProps(wings)} /> : null}
      <div className={styles.collectionHeading}>
        <CollectionHeader
          heading={collection.title}
          subText={collection.seoTextA}
          navigation={shouldShowNav ? <CollectionHeaderNav metafieldCollection={collection} /> : undefined}
        />
        <SponsoredContentContainer />
        <SearchContentContainer term={collection.title} />
        {preezieFinderProps?.placement === 'Top' ? preezieFinder : null}
        {leaderboards?.length ? (
          <div className={styles.leaderboard}>
            <CollectionLeaderboardBanner
              {...LeaderboardBannerTransformer.toCollectionLeaderboardBannerProps(leaderboards[0], 0)}
            />
          </div>
        ) : null}
      </div>
      <RefinementContainer
        facets={facets}
        collection={collection}
        indexName={indexName}
        priceRange={undefined}
        isSearchPage={false}
        nbSaleItems={nbSaleItems}
        nbClearanceItems={nbClearanceItems}
      />
      <CustomInfiniteHits
        collection={collection}
        preezieFinder={preezieFinderProps?.placement === 'Middle' ? preezieFinder : null}
        shouldSplitTiles={shouldSplitTiles}
        isMerchStudio={isMerchStudio}
        onHasNoResults={() => {
          if (isMerchStudio && !hasNoMerchStudioHits) {
            setHasNoMerchStudioHits(true);
          }
        }}
      />
      {preezieFinderProps?.placement === 'Bottom' ? preezieFinder : null}
      {collection.seoTextB ? (
        <div className={styles.seoTextB}>
          <div dangerouslySetInnerHTML={{ __html: collection.seoTextB }} />
        </div>
      ) : null}
      <Disclaimer disclaimers={window.Jbhifi.disclaimers} />
    </InstantSearch>
  );
};

export default CollectionContainer;
